import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Loader2, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useBranding, buildBrandingUrl } from '../contexts/BrandingContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const BG_IMAGE = 'https://customer-assets.emergentagent.com/job_4dc508e2-00ad-4791-a42f-c0685c15cd50/artifacts/w7lm448x_background.png';
const COMPANY_ID = 1;

// Keys used to pass data to next screens via localStorage
export const REG_STORAGE_KEY = 'wdReg';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const { branding } = useBranding();
  const logoSrc = buildBrandingUrl(branding?.app_logo || branding?.logo_url);
  const brandName = branding?.brand_name || 'WingsDiet';

  const [view, setView] = useState('getStarted'); // 'getStarted' | 'emailInput'
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [authConfig, setAuthConfig] = useState(null); // { google: { enabled, client_id }, apple: { enabled, service_id, redirect_uri } }
  const googleProcessed = useRef(false);
  const gisInitialized = useRef(false);

  useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);

  // ── Load auth config from backend ────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/v1/public/auth-config`)
      .then(({ data }) => setAuthConfig(data))
      .catch(() => setAuthConfig({ google: { enabled: true, client_id: '' }, apple: { enabled: false, service_id: '' } }));
  }, []);

  // ── Load Google Identity Services (GIS) when custom client_id is configured ──
  useEffect(() => {
    if (!authConfig?.google?.enabled || !authConfig?.google?.client_id) return;
    if (gisInitialized.current) return;
    gisInitialized.current = true;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: authConfig.google.client_id,
        callback: handleGisCredential,
        auto_select: false,
      });
    };
    document.head.appendChild(script);
  }, [authConfig]); // eslint-disable-line

  // ── Load Apple Sign In JS (when Apple is enabled) ─────────────────────────────
  useEffect(() => {
    if (!authConfig?.apple?.enabled || !authConfig?.apple?.service_id) return;

    const existing = document.querySelector('script[src*="appleid.cdn-apple.com"]');
    if (existing) return;

    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => {
      // Always use the public-facing origin for the redirect URI
      const appleRedirectUri = authConfig.apple.redirect_uri || `${window.location.origin}/api/v1/public/apple-callback`;
      window.AppleID?.auth.init({
        clientId: authConfig.apple.service_id,
        scope: 'name email',
        redirectURI: appleRedirectUri,
        usePopup: true,
      });
    };
    document.head.appendChild(script);
  }, [authConfig]);

  // ── Handle Emergent Google OAuth callback (session_id in URL hash) ────────────
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash.includes('session_id=')) return;
    if (googleProcessed.current) return;
    googleProcessed.current = true;

    const sessionId = hash.split('session_id=')[1]?.split('&')[0];
    if (!sessionId) return;

    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    setGoogleLoading(true);
    axios.post(`${API}/api/v1/public/google-register`, {
      session_id: sessionId,
      company_id: COMPANY_ID,
    })
      .then(({ data }) => {
        if (!data.isNew) {
          toast.error('This email is already registered. Please sign in.');
          return;
        }
        localStorage.setItem(REG_STORAGE_KEY, JSON.stringify({
          token: data.accessToken,
          userId: data._id,
          email: data.email,
          name: data.name || '',
          loginType: 'google',
          refCode,
        }));
        navigate('/register/wizard');
      })
      .catch(err => {
        toast.error(err.response?.data?.message || 'Google sign-in failed. Please try again.');
      })
      .finally(() => setGoogleLoading(false));
  }, []); // eslint-disable-line

  // ── GIS credential callback (custom Google client_id flow) ───────────────────
  const handleGisCredential = async (response) => {
    setGoogleLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/v1/public/custom-google-register`, {
        id_token: response.credential,
        company_id: COMPANY_ID,
      });
      if (!data.isNew) {
        toast.error('This email is already registered. Please sign in.');
        return;
      }
      localStorage.setItem(REG_STORAGE_KEY, JSON.stringify({
        token: data.accessToken,
        userId: data._id,
        email: data.email,
        name: data.name || '',
        loginType: 'google',
        refCode,
      }));
      navigate('/register/wizard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // ── Continue with Google ───────────────────────────────────────────────────
  const handleGoogle = () => {
    if (authConfig?.google?.client_id) {
      // Use Google Identity Services popup with custom client_id
      if (window.google?.accounts?.id) {
        window.google.accounts.id.prompt();
      } else {
        toast.error('Google sign-in is loading, please try again in a moment.');
      }
    } else {
      // Fall back to Emergent-managed Google Auth
      const redirectUrl = window.location.origin + '/register';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  // ── Continue with Apple ────────────────────────────────────────────────────
  const handleApple = async () => {
    if (!window.AppleID) {
      toast.error('Apple Sign-in is loading, please try again in a moment.');
      return;
    }
    setAppleLoading(true);
    try {
      const response = await window.AppleID.auth.signIn();
      const { id_token, code } = response.authorization;
      const appleUser = response.user || null;

      const { data } = await axios.post(`${API}/api/v1/public/apple-callback`, {
        id_token,
        code,
        user: appleUser,
        company_id: COMPANY_ID,
      });

      if (!data.isNew) {
        toast.error('This email is already registered. Please sign in.');
        return;
      }
      localStorage.setItem(REG_STORAGE_KEY, JSON.stringify({
        token: data.accessToken,
        userId: data._id,
        email: data.email,
        name: data.name || '',
        loginType: 'apple',
        refCode,
      }));
      navigate('/register/wizard');
    } catch (err) {
      if (err.error === 'popup_closed_by_user') return; // user cancelled
      toast.error(err.response?.data?.message || 'Apple sign-in failed. Please try again.');
    } finally {
      setAppleLoading(false);
    }
  };

  // ── Continue with Email ────────────────────────────────────────────────────
  const handleEmailContinue = async () => {
    setEmailError('');
    const trimmed = email.trim().toLowerCase();
    const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmed) { setEmailError('Please enter your email address'); return; }
    if (!emailReg.test(trimmed)) { setEmailError('Please enter a valid email address'); return; }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/v1/mobile/auth/registerPhone`, {
        email: trimmed,
        loginType: 'email',
        company_id: COMPANY_ID,
      });

      if (!data.isNew) {
        setEmailError('This email is already registered. Please sign in instead.');
        setLoading(false);
        return;
      }

      localStorage.setItem(REG_STORAGE_KEY, JSON.stringify({
        token: data.accessToken,
        userId: String(data._id),
        email: trimmed,
        name: '',
        loginType: 'email',
        refCode,
      }));
      navigate('/register/wizard');
    } catch (err) {
      setEmailError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Determine which social buttons to show
  const showGoogle = authConfig?.google?.enabled !== false; // show unless explicitly disabled
  const showApple = authConfig?.apple?.enabled && authConfig?.apple?.service_id;

  if (googleLoading) {
    return (
      <div style={{ ...pageStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,8,14,0.3)', pointerEvents: 'none' }} />
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 32px' }}>
          <Loader2 size={36} color="#1a1a2e" style={{ animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#374151', fontWeight: 600, fontSize: 15 }}>Signing in with Google...</p>
        </div>
        <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      </div>
    );
  }

  return (
    <div data-testid="register-page" style={pageStyle}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,8,14,0.3)', pointerEvents: 'none' }} />

      <div
        data-testid="register-card"
        style={{
          ...cardStyle,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}
      >
        {/* ── GET STARTED VIEW ── */}
        {view === 'getStarted' && (
          <div style={{ textAlign: 'center' }}>
            {/* Logo */}
            <div style={{ marginBottom: 20 }}>
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={brandName}
                  style={{ height: 100, maxWidth: 220, objectFit: 'contain', margin: '0 auto' }}
                />
              ) : (
                <div style={{
                  width: 100, height: 100, borderRadius: 24,
                  background: 'linear-gradient(135deg, #1e3a8a, #2563eb)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto',
                  boxShadow: '0 8px 24px rgba(37,99,235,0.3)',
                }}>
                  <Mail size={44} color="#fff" />
                </div>
              )}
            </div>

            {/* Brand name */}
            <p style={{
              fontSize: 22, fontWeight: 800, color: '#2563eb',
              margin: '0 0 6px', letterSpacing: '-0.3px',
            }}>
              {brandName}
            </p>

            {/* Tagline */}
            <h1 style={{
              fontSize: 32, fontWeight: 900, lineHeight: 1.2,
              margin: '0 0 36px', letterSpacing: '-0.5px',
            }}>
              <span style={{ color: '#1a1a2e' }}>Start your </span>
              <br />
              <span style={{ color: '#f97316' }}>Fitness </span>
              <span style={{ color: '#1a1a2e' }}>Journey</span>
            </h1>

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Google */}
              {showGoogle && (
                <button
                  data-testid="signup-google-btn"
                  onClick={handleGoogle}
                  disabled={googleLoading}
                  style={googleBtnStyle}
                  onMouseEnter={e => { e.currentTarget.style.background = '#16213e'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#1a2744'; }}
                >
                  <GoogleIcon />
                  <span>Continue with Google</span>
                </button>
              )}

              {/* Apple */}
              {showApple && (
                <button
                  data-testid="signup-apple-btn"
                  onClick={handleApple}
                  disabled={appleLoading}
                  style={socialBtnStyle}
                  onMouseEnter={e => { e.currentTarget.style.background = '#e9e9eb'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#f2f2f7'; }}
                >
                  {appleLoading
                    ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    : (
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="#1a1a2e">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                    )
                  }
                  <span>Continue with Apple</span>
                </button>
              )}

              {/* Email */}
              <button
                data-testid="signup-email-btn"
                onClick={() => setView('emailInput')}
                style={socialBtnStyle}
                onMouseEnter={e => { e.currentTarget.style.background = '#e9e9eb'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#f2f2f7'; }}
              >
                <Mail size={19} color="#1a1a2e" />
                <span>Continue with E-Mail</span>
              </button>
            </div>

          </div>
        )}

        {/* ── EMAIL INPUT VIEW ── */}
        {view === 'emailInput' && (
          <div>
            <button
              onClick={() => { setView('getStarted'); setEmailError(''); setEmail(''); }}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 20 }}
            >
              <ChevronLeft size={16} /> Back
            </button>

            {/* Logo small */}
            {logoSrc && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <img src={logoSrc} alt={brandName} style={{ height: 44, maxWidth: 130, objectFit: 'contain' }} />
              </div>
            )}

            <h2 style={{ fontWeight: 900, fontSize: 22, color: '#1a1a2e', textAlign: 'center', margin: '0 0 4px', letterSpacing: '-0.3px' }}>
              Enter your Email
            </h2>
            <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, marginBottom: 24 }}>
              We'll check if you have an account
            </p>

            <div style={{ marginBottom: 6 }}>
              <label style={labelStyle}>Email Address</label>
              <div style={{ position: 'relative' }}>
                <Mail size={15} color="#9ca3af" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input
                  data-testid="register-email-input"
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleEmailContinue()}
                  placeholder="you@example.com"
                  autoFocus
                  autoComplete="email"
                  style={inputStyle}
                  onFocus={e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
                  onBlur={e => { e.target.style.borderColor = emailError ? '#ef4444' : '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              {emailError && (
                <p data-testid="email-error" style={{ color: '#ef4444', fontSize: 12, marginTop: 6, fontWeight: 500 }}>
                  {emailError}
                </p>
              )}
            </div>

            <button
              data-testid="email-continue-btn"
              onClick={handleEmailContinue}
              disabled={loading}
              style={{
                ...primaryBtnStyle,
                width: '100%',
                marginTop: 16,
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading
                ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Checking...</>
                : 'Continue'
              }
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        input::placeholder { color: #d1d5db !important; }
      `}</style>
    </div>
  );
}

// ── Google "G" icon ───────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
    </svg>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const pageStyle = {
  minHeight: '100vh',
  backgroundImage: `url(${BG_IMAGE})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  padding: '24px 16px',
  fontFamily: "'Nunito', 'Inter', sans-serif",
};

const cardStyle = {
  position: 'relative',
  zIndex: 1,
  width: '100%',
  maxWidth: 420,
  background: 'rgba(255,255,255,0.96)',
  backdropFilter: 'blur(28px)',
  WebkitBackdropFilter: 'blur(28px)',
  borderRadius: 24,
  padding: '36px 32px 32px',
  boxShadow: '0 8px 48px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,1)',
  border: '1px solid rgba(255,255,255,0.65)',
};

const googleBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
  padding: '14px 20px',
  background: '#1a2744',
  color: '#fff',
  border: 'none', borderRadius: 12,
  fontSize: 14, fontWeight: 700,
  cursor: 'pointer',
  transition: 'background 0.2s ease',
  boxShadow: '0 4px 16px rgba(26,39,68,0.3)',
};

const socialBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
  padding: '13px 20px',
  background: '#f2f2f7',
  color: '#1a1a2e',
  border: '1.5px solid #e5e7eb', borderRadius: 12,
  fontSize: 14, fontWeight: 700,
  cursor: 'pointer',
  transition: 'background 0.2s ease',
};

const primaryBtnStyle = {
  padding: '13px 0',
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 55%, #0f3460 100%)',
  color: '#fff',
  border: 'none', borderRadius: 12,
  fontSize: 13, fontWeight: 800,
  letterSpacing: '0.8px', textTransform: 'uppercase',
  cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  boxShadow: '0 4px 18px rgba(10,20,60,0.3)',
};

const labelStyle = {
  display: 'block', color: '#374151',
  fontSize: 11, fontWeight: 700,
  marginBottom: 5, letterSpacing: '0.5px', textTransform: 'uppercase',
};

const inputStyle = {
  width: '100%',
  padding: '11px 12px 11px 38px',
  background: '#fafafa',
  border: '1.5px solid #e5e7eb',
  borderRadius: 10,
  color: '#111827', fontSize: 14,
  boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  outline: 'none',
};
